// client/src/components/pages/Settings/LexiStarDisplayRules/ConditionRows.tsx
//
// The "when" of a map colour or mark rule (ST-00276 phase 2): the notification rule builder's
// condition rows (field · how to compare · value, operators in its words; NotificationRules/
// RuleBuilder), matched all / any, plus "recent event" rows (an event in the last N minutes /
// hours / days). The field picker offers only the fields the organisation's devices report and
// the events it has had (displayRulesLogic fieldChoices). A condition that nests deeper than one
// all / any of rows (written through the API) is shown in words, with a way to start it over.

import { FaPlus, FaTimes } from 'react-icons/fa';
import { ThemedMultiSelect, ThemedSelect } from '../../../../types/commonComponents';
import { inputClasses } from '../../LexiStar/StatusBoard/atoms';
import {
  LIST_OPS,
  NO_VALUE_OPS,
  describeCondition,
  type Condition,
  type ConditionOp,
  type FieldCondition,
  type Scalar,
} from '../../../map/displayRules';
import type { DisplayCatalogue } from '../../../../types/lexistar-display-rules';
import {
  WINDOW_SECONDS,
  fieldChoices,
  fieldOf,
  isEventLeaf,
  newRow,
  opChoices,
  parseValue,
  rowField,
  simpleOf,
  whenOf,
  windowParts,
  withOp,
  type Leaf,
  type SimpleCondition,
  type WindowUnit,
} from './displayRulesLogic';

const fixedInput = inputClasses.replace('w-full ', '');

function ValueEditor({
  row,
  catalogue,
  disabled,
  label,
  onChange,
}: {
  row: FieldCondition;
  catalogue: DisplayCatalogue | null;
  disabled: boolean;
  label: string;
  onChange: (next: FieldCondition) => void;
}) {
  const field = fieldOf(catalogue, row.path);
  if (NO_VALUE_OPS.includes(row.op)) return null;
  const options = field?.options;
  if (LIST_OPS.includes(row.op)) {
    const values = (Array.isArray(row.value) ? row.value : []).map(String);
    if (options && options.length) {
      return (
        <div className="flex-1 min-w-[12rem]">
          <ThemedMultiSelect
            isMulti
            aria-label={label}
            value={values}
            disabled={disabled}
            onChange={(e) => onChange({ ...row, value: e.target.value })}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </ThemedMultiSelect>
        </div>
      );
    }
    return (
      <input
        aria-label={label}
        value={values.join(', ')}
        disabled={disabled}
        placeholder="a, b, c"
        onChange={(e) =>
          onChange({
            ...row,
            value: e.target.value
              .split(',')
              .map((x) => x.trim())
              .filter(Boolean)
              .map((x) => parseValue(field?.kind, x)),
          })
        }
        className={`${inputClasses} flex-1 min-w-[10rem]`}
      />
    );
  }
  const text = row.value === undefined || row.value === null ? '' : String(row.value);
  if (field?.kind === 'boolean') {
    return (
      <div className="w-32">
        <ThemedSelect
          aria-label={label}
          value={text}
          disabled={disabled}
          onChange={(e) => onChange({ ...row, value: String(e.target.value) === 'true' })}
        >
          <option value="true">yes</option>
          <option value="false">no</option>
        </ThemedSelect>
      </div>
    );
  }
  if (options && options.length) {
    return (
      <div className="flex-1 min-w-[12rem]">
        <ThemedSelect aria-label={label} value={text} disabled={disabled} onChange={(e) => onChange({ ...row, value: String(e.target.value) })}>
          <option value="">Choose…</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </ThemedSelect>
      </div>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 flex-1 min-w-[8rem]">
      <input
        aria-label={label}
        type={field?.kind === 'number' ? 'number' : 'text'}
        inputMode={field?.kind === 'number' ? 'decimal' : undefined}
        value={text}
        disabled={disabled}
        onChange={(e) => onChange({ ...row, value: parseValue(field?.kind, e.target.value) as Scalar })}
        className={`${fixedInput} w-full`}
      />
      {field?.unit && <span className="text-xs text-slate-500 whitespace-nowrap">{field.unit}</span>}
    </span>
  );
}

function Row({
  row,
  index,
  catalogue,
  disabled,
  onChange,
  onRemove,
}: {
  row: Leaf;
  index: number;
  catalogue: DisplayCatalogue | null;
  disabled: boolean;
  onChange: (next: Leaf) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const field = rowField(row);
  const groups = fieldChoices(catalogue, field);
  return (
    <div className="flex flex-wrap items-center gap-2" data-condition-row={field}>
      <div className="w-full sm:w-64">
        <ThemedSelect
          aria-label={`Condition ${n} field`}
          value={field}
          disabled={disabled}
          onChange={(e) => onChange(newRow(String(e.target.value)))}
        >
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ))}
        </ThemedSelect>
      </div>
      {isEventLeaf(row) ? (
        <EventWindow row={row} n={n} disabled={disabled} onChange={onChange} />
      ) : (
        <>
          <div className="w-40">
            <ThemedSelect
              aria-label={`Condition ${n} operator`}
              value={row.op}
              disabled={disabled}
              onChange={(e) => onChange(withOp(row, String(e.target.value) as ConditionOp))}
            >
              {opChoices(row.path).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </ThemedSelect>
          </div>
          <ValueEditor row={row} catalogue={catalogue} disabled={disabled} label={`Condition ${n} value`} onChange={onChange} />
        </>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove condition ${n}`}
          className="touch-target p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-slate-100"
        >
          <FaTimes size={12} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function EventWindow({
  row,
  n,
  disabled,
  onChange,
}: {
  row: { event: string; within_s: number };
  n: number;
  disabled: boolean;
  onChange: (next: Leaf) => void;
}) {
  const { amount, unit } = windowParts(row.within_s);
  const set = (a: number, u: WindowUnit) =>
    onChange({ event: row.event, within_s: Math.max(60, Math.round((Number.isFinite(a) ? a : 1) * WINDOW_SECONDS[u])) });
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-600">
      in the last
      <input
        aria-label={`Condition ${n} how far back`}
        type="number"
        min={1}
        value={amount}
        disabled={disabled}
        onChange={(e) => set(Number(e.target.value), unit)}
        className={`${fixedInput} w-20`}
      />
      <div className="w-28">
        <ThemedSelect aria-label={`Condition ${n} unit`} value={unit} disabled={disabled} onChange={(e) => set(amount, String(e.target.value) as WindowUnit)}>
          <option value="min">minutes</option>
          <option value="h">hours</option>
          <option value="d">days</option>
        </ThemedSelect>
      </div>
    </span>
  );
}

export default function ConditionRows({
  when,
  catalogue,
  disabled,
  onChange,
}: {
  when: Condition;
  catalogue: DisplayCatalogue | null;
  disabled: boolean;
  onChange: (next: Condition) => void;
}) {
  const simple = simpleOf(when);
  if (!simple) {
    return (
      <div className="space-y-2" role="group" aria-label="Conditions">
        <p className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 ring-1 ring-inset ring-slate-200">{describeCondition(when)}</p>
        {!disabled && (
          <button type="button" onClick={() => onChange({ always: true })} className="text-xs font-semibold text-primary-700 hover:underline">
            These conditions nest too deep to edit here: start them over
          </button>
        )}
      </div>
    );
  }
  const set = (next: SimpleCondition) => onChange(whenOf(next));
  const firstField = fieldChoices(catalogue)[0]?.options[0]?.value ?? 'context';
  return (
    <div className="space-y-2" role="group" aria-label="Conditions">
      {simple.rows.length > 1 && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          Match
          <div className="w-28">
            <ThemedSelect aria-label="Match" value={simple.mode} disabled={disabled} onChange={(e) => set({ ...simple, mode: String(e.target.value) as 'all' | 'any' })}>
              <option value="all">all</option>
              <option value="any">any</option>
            </ThemedSelect>
          </div>
          of these
        </div>
      )}
      {simple.rows.length === 0 && <p className="text-sm text-slate-500">Always (every truck the rules above did not take).</p>}
      {simple.rows.map((r, i) => (
        <Row
          key={i}
          row={r}
          index={i}
          catalogue={catalogue}
          disabled={disabled}
          onChange={(next) => set({ ...simple, rows: simple.rows.map((x, j) => (j === i ? next : x)) })}
          onRemove={() => set({ ...simple, rows: simple.rows.filter((_, j) => j !== i) })}
        />
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={() => set({ ...simple, rows: [...simple.rows, newRow(firstField)] })}
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
        >
          <FaPlus size={9} aria-hidden="true" /> Add condition
        </button>
      )}
    </div>
  );
}
